import { useCallback } from 'react';
import { Modal } from 'is24-corecss';
import HowItWorksTab from './components/HowItWorksTab';
import OverviewTab from './components/OverviewTab';
import { Benefit } from '@/@types/benefits';
import { InsuranceServiceName } from '@/@types/insurance';
import { ProductType } from '@/@types/product';
import { WebPImageElement } from '@/components/common/display/imageSource/ImageSource';
import { useIsServiceActive } from '@/customHooks/useInsurancePackageStatus';
import { useIntl } from '@/i18n/hooks/useIntl';
import { campaigns } from '@/components/common/campaigns';
import { useGetHasProduct, useHasWohnenPlusLight } from '@/customHooks/useGetHasProduct.hook';
import { useUrlState } from '@/customHooks/useUrlState.hook';
import { TrackingEvents } from '@/tracking/trackingEvents';
import { useReportTrackingTenant } from '@/customHooks/useReportTrackingTenant.hook';
import ModalV2 from '@/components/common/display/modal/ModalV2';

export interface AragModalProps {
  serviceName: InsuranceServiceName;
  image: string;
  imageWebp?: WebPImageElement;
  imageMobile?: string;
  imageMobileWebp?: WebPImageElement;
  howItWorksImage1Png: string;
  howItWorksImage1WebP?: WebPImageElement;
  howItWorksImage2Png: string;
  howItWorksImage2WebP?: WebPImageElement;
  isVisible: boolean;
  additionalParagraph?: boolean;
  onClose: () => void;
}

export const AragModal = ({
  serviceName,
  image,
  imageWebp,
  imageMobile,
  imageMobileWebp,
  howItWorksImage1Png,
  howItWorksImage1WebP,
  howItWorksImage2Png,
  howItWorksImage2WebP,
  isVisible,
  additionalParagraph,
  onClose,
}: AragModalProps) => {
  const { t } = useIntl();
  const hasWohnenPlus = useGetHasProduct(ProductType.WohnenPlus);
  const hasWohnenPlusLight = useHasWohnenPlusLight();
  const isEntitled = useIsServiceActive(serviceName);
  const { currentUrl, isMFE } = useUrlState();
  const { reportTenantTracking } = useReportTrackingTenant();

  const handleLinkClick = useCallback(() => {
    if (!hasWohnenPlus) {
      reportTenantTracking(TrackingEvents.TENANT.DISCOVER_WOHNEN);
      const serviceKey = isMFE ? (`${serviceName}_MFE` as keyof typeof campaigns) : serviceName;
      const returnUrl = isMFE && currentUrl ? `&returnUrl=${encodeURIComponent(currentUrl)}` : '';
      window.location.href = `${campaigns[serviceKey]}${returnUrl}`;
    }
  }, [hasWohnenPlus, serviceName, isMFE, currentUrl, reportTenantTracking]);

  const shouldShowPrimaryButton = !hasWohnenPlus && !hasWohnenPlusLight;

  return (
    <ModalV2
      title={t({ id: `benefits.${serviceName.toLowerCase()}.headline` })}
      isVisible={isVisible}
      image={image}
      imageWebp={imageWebp}
      imageMobile={imageMobile}
      imageMobileWebp={imageMobileWebp}
      imageAlt={t({
        id: `benefits.${serviceName.toLowerCase()}.modal.image`,
      })}
      imageUseFullWidth={true}
      tabsContent={[
        <OverviewTab
          key='overview'
          isEntitled={isEntitled}
          serviceName={serviceName}
          additionalParagraph={additionalParagraph}
        />,
        <HowItWorksTab
          key='howItWorks'
          serviceName={serviceName.toLowerCase()}
          image1={howItWorksImage1Png}
          imageWebp1={howItWorksImage1WebP}
          image2={howItWorksImage2Png}
          imageWebp2={howItWorksImage2WebP}
        />,
      ]}
      benefit={Benefit[serviceName]}
      onClose={onClose}
      {...(shouldShowPrimaryButton && {
        primaryFooterBtn: (
          <Modal.FooterPrimaryBtn
            label={t({
              id: `common.buttons.discoverWohnenPlus`,
              defaultMessage: 'WohnenPlus entdecken',
            })}
            onClick={handleLinkClick}
          />
        ),
      })}
    />
  );
};
